import { AfterViewInit, Component, Inject, OnInit } from '@angular/core';
import * as angular from 'angular';
import { downgradeComponent } from '@angular/upgrade/static';
import { Store } from '@ngrx/store';
import { GenericListType, LoadMoreQuery } from '../../common/components/lists/generic-list.component';
import { AppState } from '../../store';
import {
  closeWorkspacesModal,
  getWorkspacesList,
  loadMoreWorkspacesList,
  openWorkspacesModal,
  searchWorkspacesList,
} from '../store/workspaces.actions';
import {
  selectAreAllWorkspacesLoaded,
  selectWorkspaces,
  selectWorkspacesErrorFlag,
  selectWorkspacesIsModalOpenFlag,
  selectWorkspacesLoadingFlag,
} from '../store/workspaces.selectors';
import { Workspace } from '../workspaces.models';
import { AssistedSearchWorkspacesConfiguratorService } from '../../common/services/assisted-search/assisted-search-workspaces-configurator.service';
import { SearchConfig } from '../../common/services/assisted-search/search-config.model';
import { QueryParamsWorkspacesList } from '../../common/helper/query-params-generic-list.helper';
import { WorkspacesResource } from '../resources/workspaces.resource';
import { NotifyService } from '../../common/services/notify.service';
import { Package } from '../../packages/package.models';

@Component({
  selector: 'workspaces-list',
  template: `
    <generic-list
      [type]="type"
      [items]="workspaces$ | async"
      [isLoading]="workspacesLoading$ | async"
      [hasNewButton]="true"
      (createClick)="openCreateModal()"
      [newBtnText]="'workspace.generic-object.buttons.new' | translate"
      [emptyTitle]="'workspace.generic-object.empty.title' | translate"
      [emptyDescription]="'workspace.generic-object.empty.description' | translate"
      (loadMore)="loadMoreWorkspaces($event)"
      [allItemsLoaded]="areAllWorkspacesLoaded$ | async"
      [limit]="queryParams.limit"
      [searchPlaceholder]="'workspace.generic-object.placeholders.search' | translate"
      [searchConfig]="searchConfig"
      [searchQueryFromUrl]="searchQueryFromUrl"
      [hasSearch]="true"
      [isError]="workspacesLoadingError$ | async"
      (searchSubmit)="onSearchSubmit($event)"
    >
      <ng-template templateName="listItem" let-item>
        <workspace-list-item [item]="item" (edit)="editWorkspace($event)"></workspace-list-item>
      </ng-template>
    </generic-list>
    <xp-modal
      [isOpen]="isModalOpen$ | async"
      [closeButtonText]="'workspace.generic-object.buttons.close' | translate"
      [saveButtonText]="'workspace.generic-object.buttons.save' | translate"
      [titleText]="'workspace.generic-object.title' | translate"
      (close)="onModalClose()"
    >
      <ng-template>
        <workspace-form [item]="formItem" [isLoadingItem]="isLoadingItem"></workspace-form>
      </ng-template>
    </xp-modal>
  `,
})
export class WorkspacesListComponent implements AfterViewInit, OnInit {
  type = GenericListType.workspaces;
  workspacesLoading$ = this.store.select(selectWorkspacesLoadingFlag);
  workspacesLoadingError$ = this.store.select(selectWorkspacesErrorFlag);
  isModalOpen$ = this.store.select(selectWorkspacesIsModalOpenFlag);
  workspaces$ = this.store.select(selectWorkspaces);
  areAllWorkspacesLoaded$ = this.store.select(selectAreAllWorkspacesLoaded);
  queryParams = QueryParamsWorkspacesList;
  formItem: Partial<Workspace> = {};
  searchQueryFromUrl = '';
  searchConfig: SearchConfig = {};
  isLoadingItem = false;

  constructor(
    @Inject('$rootScope') private $rootScope: any,
    private store: Store<AppState>,
    private assistedSearchWorkspacesConfigurator: AssistedSearchWorkspacesConfiguratorService,
    @Inject('$stateParams') private $stateParams: any,
    private workspacesResource: WorkspacesResource,
    private notify: NotifyService,
  ) {}

  ngOnInit() {
    if (this.$stateParams.account_id.includes('intentwise')) {
      this.queryParams.include = '';
    }
    const query = this.$rootScope.getQueryParams('query');

    this.searchQueryFromUrl = query || '';

    this.assistedSearchWorkspacesConfigurator.getConfig$().subscribe((config) => {
      this.searchConfig = config;
    });

    if (!query) {
      this.store.dispatch(getWorkspacesList({ params: this.queryParams, isWorkspacesView: true }));
    }
  }

  ngAfterViewInit() {
    if (this.$rootScope.stateIs('app.account.workspaces.new')) {
      this.openCreateModal();
    }
  }

  editWorkspace(item: Workspace) {
    this.workspacesResource.get(item.id, { include: 'packages' } as any).subscribe({
      next: (workspaceItem) => {
        this.formItem = {
          ...workspaceItem,
          packages: (workspaceItem.packages || []).filter((packageItem) => packageItem.package.status !== 'archived'),
        };
        this.isLoadingItem = false;
      },
      error: () => {
        this.notify.error('An error occurred while loading workspace item.');
        this.isLoadingItem = false;
      },
    });
    this.formItem = {
      ...item,
      packages: (item.packages || []).filter((packageItem) => packageItem.package.status !== 'archived'),
    };
    this.isLoadingItem = true;
    this.store.dispatch(openWorkspacesModal());
  }

  openCreateModal(): void {
    this.formItem = {};
    this.$rootScope.stateGo('app.account.workspaces.new');
    this.store.dispatch(openWorkspacesModal());
  }

  onModalClose(): void {
    this.$rootScope.stateGo('app.account.workspaces');
    this.store.dispatch(closeWorkspacesModal());
  }

  loadMoreWorkspaces(query: LoadMoreQuery) {
    const params: any = {
      ...this.queryParams,
      ...query,
    };
    this.store.dispatch(loadMoreWorkspacesList({ params }));
  }

  onSearchSubmit(query: string) {
    const params = { ...this.queryParams, q: query };
    this.queryParams = params;

    if (!query) {
      this.store.dispatch(getWorkspacesList({ params: this.queryParams }));
    } else {
      this.store.dispatch(searchWorkspacesList({ params }));
    }
  }
}

((window as any).xplenty as angular.IModule).directive(
  'workspacesList',
  downgradeComponent({
    component: WorkspacesListComponent,
  }),
);
